import { useState, useEffect, useCallback } from "react";
import { Radio, Settings, ExternalLink, Clock, Image, Trash2, Zap, Eye, EyeOff, BarChart3, Users, Palette, Wifi, Check, ChevronRight, AlertCircle } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

export const StationTab = ({ currentUser, stats, authToken }) => {
  // Station request state
  const [stationRequest, setStationRequest] = useState(null);
  const [stationIdInput, setStationIdInput] = useState('');
  const [stationNameInput, setStationNameInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [editingStationName, setEditingStationName] = useState(false);
  const [stationNameSaving, setStationNameSaving] = useState(false);

  // Metadata state
  const [stationMetadata, setStationMetadata] = useState(null);
  const [editingMetadata, setEditingMetadata] = useState(false);
  const [metadataForm, setMetadataForm] = useState({ name: '', description: '', genre: '', url: '' });
  const [metadataSaving, setMetadataSaving] = useState(false);

  // Branding state
  const [brandingData, setBrandingData] = useState(null);
  const [brandingTagline, setBrandingTagline] = useState('');
  const [brandingColor, setBrandingColor] = useState('#a855f7');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [brandingSuccess, setBrandingSuccess] = useState('');
  const [brandingError, setBrandingError] = useState('');
  const [playerConfig, setPlayerConfig] = useState({ show_fft: true, show_history: true, show_listeners: true, player_card_color: '#1a1a3e' });

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (authToken) h['Authorization'] = `Bearer ${authToken}`;
    return h;
  }, [authToken]);

  // Fetch station request on mount
  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/station/request`, { credentials: 'omit', headers: headers() });
        if (res.ok) {
          const data = await res.json();
          if (data.has_request) setStationRequest(data.request);
        }
      } catch {}
    };
    fetchRequest();
  }, [headers]);

  // Fetch branding when station is connected
  useEffect(() => {
    if (currentUser?.azuracast_station_id) {
      fetchBranding();
    }
  }, [currentUser?.azuracast_station_id]);

  const handleRequestStation = async () => {
    if (!stationNameInput.trim()) { setSubmitError('Please enter a station name'); return; }
    setSubmitting(true); setSubmitError(''); setSubmitSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/request`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ station_name: stationNameInput, payment_method: paymentMethod })
      });
      if (res.ok) {
        const data = await res.json();
        setStationRequest({ status: 'pending', station_name: stationNameInput, payment_method: paymentMethod, created_at: new Date().toISOString() });
        setSubmitSuccess(data.message);
        setTimeout(() => {
          if (paymentMethod === 'cashapp') window.open('https://cash.app/$garynuhayden1', '_blank');
          else if (paymentMethod === 'paypal') window.open('https://paypal.me/wnuv985', '_blank');
        }, 1500);
      } else { setSubmitError((await res.json()).detail || 'Failed to submit request'); }
    } catch { setSubmitError('Failed to connect to server'); }
    finally { setSubmitting(false); }
  };

  const handleConnectStation = async () => {
    if (!stationIdInput.trim()) { setSubmitError('Please enter your station ID'); return; }
    setSubmitting(true); setSubmitError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/connect?station_id=${stationIdInput}`, { method: 'PUT', headers: headers() });
      if (res.ok) { setSubmitSuccess('Station connected! Refreshing...'); setTimeout(() => window.location.reload(), 1500); }
      else { setSubmitError((await res.json()).detail || 'Failed to connect station'); }
    } catch { setSubmitError('Failed to connect to server'); }
    finally { setSubmitting(false); }
  };

  const handleUpdateStationName = async () => {
    if (!stationNameInput.trim()) { setSubmitError('Please enter a station name'); return; }
    setStationNameSaving(true); setSubmitError(''); setSubmitSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/name?name=${encodeURIComponent(stationNameInput)}`, { method: 'PUT', headers: headers() });
      if (res.ok) {
        setSubmitSuccess('Station name updated!'); setEditingStationName(false);
        if (currentUser) currentUser.station_name = stationNameInput;
        setTimeout(() => setSubmitSuccess(''), 3000);
      } else { setSubmitError((await res.json()).detail || 'Failed to update station name'); }
    } catch { setSubmitError('Failed to connect to server'); }
    finally { setStationNameSaving(false); }
  };

  const fetchStationMetadata = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/metadata`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setStationMetadata(data);
        setMetadataForm({ name: data.name || '', description: data.description || '', genre: data.genre || '', url: data.url || '' });
      }
    } catch {}
  };

  const handleUpdateMetadata = async () => {
    setMetadataSaving(true); setSubmitError(''); setSubmitSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/metadata`, { method: 'PUT', headers: headers(), body: JSON.stringify(metadataForm) });
      if (res.ok) {
        setSubmitSuccess('Station metadata updated on AzuraCast!'); setEditingMetadata(false); setStationMetadata(metadataForm);
        setTimeout(() => setSubmitSuccess(''), 3000);
      } else { setSubmitError((await res.json()).detail || 'Failed to update metadata'); }
    } catch { setSubmitError('Failed to connect to server'); }
    finally { setMetadataSaving(false); }
  };

  const fetchBranding = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/branding`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setBrandingData(data); setBrandingTagline(data.tagline || ''); setBrandingColor(data.primary_color || '#a855f7');
        setPlayerConfig({ show_fft: data.show_fft !== false, show_history: data.show_history !== false, show_listeners: data.show_listeners !== false, player_card_color: data.player_card_color || '#1a1a3e' });
      }
    } catch {}
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingLogo(true); setBrandingSuccess('');
    try {
      const formData = new FormData(); formData.append('file', file);
      const h = {}; if (authToken) h['Authorization'] = `Bearer ${authToken}`;
      const res = await fetch(`${BACKEND_URL}/api/station/branding/logo`, { method: 'POST', headers: h, body: formData });
      if (res.ok) { const data = await res.json(); setBrandingData(prev => ({ ...prev, logo_url: data.logo_url })); setBrandingSuccess('Logo uploaded!'); setTimeout(() => setBrandingSuccess(''), 3000); }
      else { const err = await res.json(); setSubmitError(err.detail || 'Failed to upload logo'); }
    } catch { setSubmitError('Failed to upload logo'); }
    finally { setUploadingLogo(false); }
  };

  const handleDeleteLogo = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/branding/logo`, { method: 'DELETE', headers: headers() });
      if (res.ok) { setBrandingData(prev => ({ ...prev, logo_url: null })); setBrandingSuccess('Logo removed!'); setTimeout(() => setBrandingSuccess(''), 3000); }
    } catch {}
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingBackground(true); setBrandingSuccess('');
    try {
      const formData = new FormData(); formData.append('file', file);
      const h = {}; if (authToken) h['Authorization'] = `Bearer ${authToken}`;
      const res = await fetch(`${BACKEND_URL}/api/station/branding/banner`, { method: 'POST', headers: h, body: formData });
      if (res.ok) { const data = await res.json(); setBrandingData(prev => ({ ...prev, background_url: data.background_url })); setBrandingSuccess('Background uploaded!'); setTimeout(() => setBrandingSuccess(''), 3000); }
      else { const err = await res.json(); setSubmitError(err.detail || 'Failed to upload background'); }
    } catch { setSubmitError('Failed to upload background'); }
    finally { setUploadingBackground(false); }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true); setBrandingSuccess(''); setBrandingError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/branding`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ primary_color: brandingColor, tagline: brandingTagline, show_fft: playerConfig.show_fft, show_history: playerConfig.show_history, show_listeners: playerConfig.show_listeners, player_card_color: playerConfig.player_card_color })
      });
      if (res.ok) { setBrandingSuccess('Branding saved!'); setTimeout(() => setBrandingSuccess(''), 3000); }
      else { setBrandingError((await res.json()).detail || 'Failed to save branding'); }
    } catch { setBrandingError('Failed to save branding'); }
    finally { setSavingBranding(false); }
  };

  const isByos = currentUser?.plan === 'byos';

  // BYOS Stream Setup State
  const [streamSettings, setStreamSettings] = useState({ server_type: 'icecast', server_url: '', server_port: 8000, mount_point: '/stream', stream_id: 1 });
  const [streamLoading, setStreamLoading] = useState(true);
  const [streamSaved, setStreamSaved] = useState(false);
  const [streamSaving, setStreamSaving] = useState(false);
  const [streamTesting, setStreamTesting] = useState(false);
  const [streamTestResult, setStreamTestResult] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [setupStep, setSetupStep] = useState(1);

  // Fetch existing stream settings for BYOS
  useEffect(() => {
    if (!isByos) { setStreamLoading(false); return; }
    fetch(`${BACKEND_URL}/api/stream/external/settings`, { headers: headers() })
      .then(r => r.json())
      .then(data => {
        if (data.server_url) { setStreamSettings(data); setStreamSaved(true); setSetupStep(3); }
        setStreamLoading(false);
      })
      .catch(() => setStreamLoading(false));
  }, [isByos, headers]);

  // Poll live status for BYOS
  useEffect(() => {
    if (!isByos || !streamSaved) return;
    const poll = () => {
      fetch(`${BACKEND_URL}/api/stream/external/status`, { headers: headers() })
        .then(r => r.json()).then(setLiveStatus).catch(() => {});
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => clearInterval(id);
  }, [isByos, streamSaved, headers]);

  const handleStreamTest = async () => {
    setStreamTesting(true); setStreamTestResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/stream/external/test`, { method: 'POST', headers: headers(), body: JSON.stringify(streamSettings) });
      const result = await res.json();
      setStreamTestResult(result);
      if (result.online) setSetupStep(2);
    } catch { setStreamTestResult({ online: false, error: 'Connection failed' }); }
    finally { setStreamTesting(false); }
  };

  const handleStreamSave = async () => {
    setStreamSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/stream/external/settings`, { method: 'PUT', headers: headers(), body: JSON.stringify(streamSettings) });
      const data = await res.json();
      if (data.status === 'success') { setStreamSaved(true); setSetupStep(3); }
    } catch {}
    finally { setStreamSaving(false); }
  };

  return (
  <div className="space-y-6">
    {/* BYOS Flow */}
    {isByos ? (
      streamLoading ? (
        <div className="text-center py-12"><Radio className="w-8 h-8 text-cyan-400 animate-pulse mx-auto" /></div>
      ) : streamSaved ? (
        <>
          {/* Stream Connected Banner */}
          <div className="bg-gradient-to-r from-cyan-500/20 to-teal-500/20 border border-cyan-500/30 rounded-2xl p-6" data-testid="byos-connected-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center"><Wifi className={`w-6 h-6 ${liveStatus?.online ? 'text-green-400' : 'text-cyan-400'}`} /></div>
                <div>
                  <h3 className="text-lg font-semibold text-white">BYOS Stream {liveStatus?.online ? 'Live' : 'Connected'}</h3>
                  <p className="text-cyan-400 text-sm">{streamSettings.server_type === 'icecast' ? 'Icecast' : 'Shoutcast'} &middot; {streamSettings.server_url}:{streamSettings.server_port}</p>
                </div>
              </div>
              {liveStatus?.online && (
                <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 px-3 py-1.5 rounded-full">
                  <Wifi className="w-3.5 h-3.5 text-green-400 animate-pulse" />
                  <span className="text-green-400 text-xs font-medium">{liveStatus.listeners} listener{liveStatus.listeners !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>

          {/* Now Playing */}
          {liveStatus?.online && liveStatus?.now_playing && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center gap-4" data-testid="byos-now-playing">
              <div className="flex items-end gap-0.5 h-8 flex-shrink-0">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-1.5 bg-cyan-400 rounded-full animate-pulse" style={{ height: `${12 + Math.random() * 20}px`, animationDelay: `${i * 120}ms` }} />
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white font-semibold truncate">{liveStatus.now_playing.title || 'Unknown Track'}</p>
                <p className="text-white/40 text-sm truncate">{liveStatus.now_playing.artist || ''} {liveStatus.stream_name ? `· ${liveStatus.stream_name}` : ''}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-white/60 text-xs">{liveStatus.bitrate ? `${liveStatus.bitrate}kbps` : ''}</p>
                {liveStatus.genre && <p className="text-white/30 text-xs">{liveStatus.genre}</p>}
              </div>
            </div>
          )}

          {/* BYOS Quick Setup Checklist */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="byos-checklist">
            <h3 className="text-white font-semibold mb-4">Setup Checklist</h3>
            <div className="space-y-3">
              {[
                { label: 'Connect your stream', done: true, desc: `${streamSettings.server_type} at ${streamSettings.server_url}:${streamSettings.server_port}` },
                { label: 'Customize your player', done: false, desc: 'Set up your white-label player page', action: 'whitelabel' },
                { label: 'Build your station page', done: false, desc: 'Design your public station page', action: 'pagebuilder' },
                { label: 'Set up your domain', done: false, desc: 'Get a custom subdomain or domain', action: 'domains' },
              ].map((item, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl transition ${item.done ? 'bg-green-500/5 border border-green-500/10' : 'bg-white/[0.02] border border-white/5 hover:border-white/10'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.done ? 'bg-green-500/20' : 'bg-white/5'}`}>
                    {item.done ? <Check className="w-4 h-4 text-green-400" /> : <span className="text-white/30 text-sm font-bold">{i + 1}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${item.done ? 'text-green-400' : 'text-white'}`}>{item.label}</p>
                    <p className="text-white/30 text-xs truncate">{item.desc}</p>
                  </div>
                  {!item.done && item.action && (
                    <button onClick={() => { /* Tab navigation handled by Dashboard */ }} className="text-cyan-400 text-xs hover:text-cyan-300 transition" data-testid={`byos-go-${item.action}`}>
                      Set up &rarr;
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Stream Settings (editable) */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-4">Stream Connection Settings</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/50 text-xs mb-1.5">Server Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {['icecast', 'shoutcast'].map(t => (
                    <button key={t} onClick={() => setStreamSettings(s => ({ ...s, server_type: t }))}
                      className={`py-2.5 rounded-xl text-sm font-medium transition ${streamSettings.server_type === t ? 'bg-cyan-500 text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
                  ))}
                </div>
              </div>
              <div><label className="block text-white/50 text-xs mb-1.5">Server URL</label><input type="text" value={streamSettings.server_url} onChange={e => setStreamSettings(s => ({ ...s, server_url: e.target.value }))} placeholder="https://myserver.com" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-cyan-500 transition text-sm" /></div>
              <div><label className="block text-white/50 text-xs mb-1.5">Port</label><input type="number" value={streamSettings.server_port} onChange={e => setStreamSettings(s => ({ ...s, server_port: parseInt(e.target.value) || 8000 }))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition text-sm" /></div>
              {streamSettings.server_type === 'icecast' ? (
                <div><label className="block text-white/50 text-xs mb-1.5">Mount Point</label><input type="text" value={streamSettings.mount_point} onChange={e => setStreamSettings(s => ({ ...s, mount_point: e.target.value }))} placeholder="/stream" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-cyan-500 transition text-sm" /></div>
              ) : (
                <div><label className="block text-white/50 text-xs mb-1.5">Stream ID (SID)</label><input type="number" value={streamSettings.stream_id} onChange={e => setStreamSettings(s => ({ ...s, stream_id: parseInt(e.target.value) || 1 }))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition text-sm" /></div>
              )}
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button onClick={handleStreamTest} disabled={streamTesting || !streamSettings.server_url} className="bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl transition text-sm" data-testid="byos-test-btn">{streamTesting ? 'Testing...' : 'Test Connection'}</button>
              <button onClick={handleStreamSave} disabled={streamSaving} className="bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl transition text-sm" data-testid="byos-save-btn">{streamSaving ? 'Saving...' : 'Save Settings'}</button>
              {streamTestResult && (
                <span className={`text-sm ${streamTestResult.online ? 'text-green-400' : 'text-red-400'}`}>
                  {streamTestResult.online ? `Connected! ${streamTestResult.listeners} listeners` : streamTestResult.error || 'Connection failed'}
                </span>
              )}
            </div>
          </div>
        </>
      ) : (
        /* BYOS Setup Wizard */
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8" data-testid="byos-setup-wizard">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-6"><Wifi className="w-10 h-10 text-cyan-400" /></div>
            <h3 className="text-2xl font-bold text-white mb-2">Connect Your Stream</h3>
            <p className="text-white/60 max-w-md mx-auto">Your BYOS Channel plan lets you connect your own Shoutcast or Icecast server. Enter your stream details below to get started.</p>
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-center gap-4 mb-8">
            {[{ n: 1, label: 'Stream Details' }, { n: 2, label: 'Test Connection' }, { n: 3, label: 'Go Live' }].map(s => (
              <div key={s.n} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${setupStep >= s.n ? 'bg-cyan-500 text-white' : 'bg-white/10 text-white/30'}`}>{setupStep > s.n ? <Check className="w-4 h-4" /> : s.n}</div>
                <span className={`text-sm hidden sm:inline ${setupStep >= s.n ? 'text-white' : 'text-white/30'}`}>{s.label}</span>
                {s.n < 3 && <ChevronRight className="w-4 h-4 text-white/20" />}
              </div>
            ))}
          </div>

          <div className="max-w-lg mx-auto space-y-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">Server Type</label>
              <div className="grid grid-cols-2 gap-3">
                {['icecast', 'shoutcast'].map(t => (
                  <button key={t} onClick={() => setStreamSettings(s => ({ ...s, server_type: t }))}
                    className={`py-3 px-4 rounded-xl text-sm font-medium transition ${streamSettings.server_type === t ? 'bg-cyan-500 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
                ))}
              </div>
            </div>
            <div><label className="block text-white/70 text-sm mb-2">Server URL</label><input type="text" value={streamSettings.server_url} onChange={e => setStreamSettings(s => ({ ...s, server_url: e.target.value }))} placeholder="https://your-server.com" data-testid="byos-url-input" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-cyan-500 transition" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-white/70 text-sm mb-2">Port</label><input type="number" value={streamSettings.server_port} onChange={e => setStreamSettings(s => ({ ...s, server_port: parseInt(e.target.value) || 8000 }))} data-testid="byos-port-input" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition" /></div>
              {streamSettings.server_type === 'icecast' ? (
                <div><label className="block text-white/70 text-sm mb-2">Mount Point</label><input type="text" value={streamSettings.mount_point} onChange={e => setStreamSettings(s => ({ ...s, mount_point: e.target.value }))} placeholder="/stream" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-cyan-500 transition" /></div>
              ) : (
                <div><label className="block text-white/70 text-sm mb-2">Stream ID</label><input type="number" value={streamSettings.stream_id} onChange={e => setStreamSettings(s => ({ ...s, stream_id: parseInt(e.target.value) || 1 }))} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition" /></div>
              )}
            </div>

            {/* Test Result */}
            {streamTestResult && (
              <div className={`p-4 rounded-xl border ${streamTestResult.online ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`} data-testid="byos-test-result">
                {streamTestResult.online ? (
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-green-400 font-semibold text-sm">Stream Connected!</p>
                      <p className="text-white/50 text-xs">{streamTestResult.listeners} listeners &middot; {streamTestResult.stream_name || streamTestResult.genre || streamSettings.server_type}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <div><p className="text-red-400 font-semibold text-sm">Connection Failed</p><p className="text-white/50 text-xs">{streamTestResult.error || 'Check your server URL and port'}</p></div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button onClick={handleStreamTest} disabled={streamTesting || !streamSettings.server_url} data-testid="byos-wizard-test-btn"
                className="flex-1 bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white py-3 rounded-xl transition font-medium">
                {streamTesting ? 'Testing...' : 'Test Connection'}
              </button>
              <button onClick={handleStreamSave} disabled={streamSaving || !streamSettings.server_url} data-testid="byos-wizard-save-btn"
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white py-3 rounded-xl transition font-medium">
                {streamSaving ? 'Saving...' : 'Save & Continue'}
              </button>
            </div>

            <p className="text-white/30 text-xs text-center">Make sure your stream server is running and accessible from the internet before testing.</p>
          </div>
        </div>
      )
    ) : currentUser.azuracast_station_id ? (
      <>
        {/* Station Active Card */}
        <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center"><Radio className="w-6 h-6 text-green-400" /></div>
            <div><h3 className="text-lg font-semibold text-white">Station Connected</h3><p className="text-green-400 text-sm">Your station is set up and ready</p></div>
          </div>
        </div>

        {/* Station Info */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Station Details</h3>
          <div className="space-y-4">
            <div><label className="block text-white/50 text-xs mb-1">Station ID</label><div className="bg-black/40 rounded-lg p-3 font-mono text-sm text-purple-400">{currentUser.azuracast_station_id}</div></div>
            <div>
              <label className="block text-white/50 text-xs mb-1">Station Name</label>
              {editingStationName ? (
                <div className="flex gap-2">
                  <input type="text" value={stationNameInput} onChange={(e) => setStationNameInput(e.target.value)} placeholder="Enter station name" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition" />
                  <button onClick={handleUpdateStationName} disabled={stationNameSaving} className="bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition">{stationNameSaving ? 'Saving...' : 'Save'}</button>
                  <button onClick={() => { setEditingStationName(false); setStationNameInput(currentUser.station_name || ''); }} className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition">Cancel</button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-black/40 rounded-lg p-3 text-white">{currentUser.station_name || 'Your Station'}</div>
                  <button onClick={() => { setStationNameInput(currentUser.station_name || ''); setEditingStationName(true); }} className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-4 py-3 rounded-lg transition">Edit</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* AzuraCast Dashboard Link */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Manage Your Station</h3>
          <p className="text-white/60 text-sm mb-4">Access your full station dashboard on DJsandMCMedia™ to manage playlists, view analytics, configure DJ access, and more.</p>
          <a href={`${stats?.azuracast_url || 'https://djsandmc.media'}/station/${stats?.numeric_station_id || currentUser.azuracast_station_id}`} target="_blank" rel="noopener noreferrer" data-testid="open-station-dashboard-btn" className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl transition"><Settings className="w-5 h-5" /> Open Station Dashboard</a>
        </div>

        {/* Update Station ID */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Update Station ID</h3>
          <p className="text-white/60 text-sm mb-4">Need to change your station ID? Enter the new ID below.</p>
          {submitError && <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-xl mb-4 text-sm">{submitError}</div>}
          {submitSuccess && <div className="bg-green-500/20 border border-green-500/50 text-green-300 px-4 py-3 rounded-xl mb-4 text-sm">{submitSuccess}</div>}
          <div className="flex gap-2">
            <input type="text" value={stationIdInput} onChange={(e) => setStationIdInput(e.target.value)} placeholder="Enter new station ID" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition" />
            <button onClick={handleConnectStation} disabled={submitting} className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl transition">{submitting ? 'Updating...' : 'Update'}</button>
          </div>
        </div>

        {/* Station Metadata Editor */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div><h3 className="text-lg font-semibold text-white">Station Info (AzuraCast)</h3><p className="text-white/50 text-sm">Edit your station's metadata - syncs directly to AzuraCast</p></div>
            {!editingMetadata && <button onClick={() => { fetchStationMetadata(); setEditingMetadata(true); }} className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-4 py-2 rounded-lg transition">Edit Info</button>}
          </div>
          {editingMetadata ? (
            <div className="space-y-4">
              <div><label className="block text-white/60 text-sm mb-2">Station Name</label><input type="text" value={metadataForm.name} onChange={(e) => setMetadataForm({...metadataForm, name: e.target.value})} placeholder="My Awesome Radio" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition" /></div>
              <div><label className="block text-white/60 text-sm mb-2">Description</label><textarea value={metadataForm.description} onChange={(e) => setMetadataForm({...metadataForm, description: e.target.value})} placeholder="The best music 24/7..." rows={3} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition resize-none" /></div>
              <div><label className="block text-white/60 text-sm mb-2">Genre</label><input type="text" value={metadataForm.genre} onChange={(e) => setMetadataForm({...metadataForm, genre: e.target.value})} placeholder="Hip Hop, R&B, Soul" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition" /></div>
              <div><label className="block text-white/60 text-sm mb-2">Website URL</label><input type="text" value={metadataForm.url} onChange={(e) => setMetadataForm({...metadataForm, url: e.target.value})} placeholder="https://myradio.com" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition" /></div>
              <div className="flex gap-2 pt-2">
                <button onClick={handleUpdateMetadata} disabled={metadataSaving} className="bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl transition flex-1">{metadataSaving ? 'Saving to AzuraCast...' : 'Save to AzuraCast'}</button>
                <button onClick={() => setEditingMetadata(false)} className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl transition">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {stationMetadata ? (
                <>
                  <div className="flex justify-between items-center py-2 border-b border-white/10"><span className="text-white/50">Name</span><span className="text-white">{stationMetadata.name || '-'}</span></div>
                  <div className="flex justify-between items-start py-2 border-b border-white/10"><span className="text-white/50">Description</span><span className="text-white text-right max-w-xs">{stationMetadata.description || '-'}</span></div>
                  <div className="flex justify-between items-center py-2 border-b border-white/10"><span className="text-white/50">Genre</span><span className="text-white">{stationMetadata.genre || '-'}</span></div>
                  <div className="flex justify-between items-center py-2"><span className="text-white/50">Website</span><span className="text-purple-400">{stationMetadata.url || '-'}</span></div>
                </>
              ) : <p className="text-white/50 text-center py-4">Click "Edit Info" to load and edit station metadata</p>}
            </div>
          )}
        </div>

        {/* Station Branding */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4"><div><h3 className="text-lg font-semibold text-white">Station Branding</h3><p className="text-white/50 text-sm">Customize your station's look with a logo and colors</p></div></div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="block text-white/60 text-sm">Station Logo</label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-black/40 rounded-xl flex items-center justify-center overflow-hidden border border-white/10">{brandingData?.logo_url ? <img src={brandingData.logo_url} alt="Station Logo" className="w-full h-full object-cover" /> : <Image className="w-8 h-8 text-white/30" />}</div>
                <div className="flex-1 space-y-2">
                  <label className="block"><input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleLogoUpload} className="hidden" id="logo-upload" /><span className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-4 py-2 rounded-lg transition cursor-pointer inline-block text-sm">{uploadingLogo ? 'Uploading...' : 'Upload Logo'}</span></label>
                  <p className="text-white/40 text-xs">JPG, PNG, GIF or WebP. Max 5MB</p>
                  {brandingData?.logo_url && <button onClick={handleDeleteLogo} className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> Remove logo</button>}
                </div>
              </div>
            </div>
            <div className="space-y-3"><label className="block text-white/60 text-sm">Station Tagline</label><input type="text" value={brandingTagline} onChange={(e) => setBrandingTagline(e.target.value)} placeholder="The best music 24/7..." maxLength={100} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition" /><p className="text-white/40 text-xs">{brandingTagline.length}/100 characters</p></div>
            <div className="space-y-3">
              <label className="block text-white/60 text-sm">Brand Color</label>
              <div className="flex items-center gap-3"><input type="color" value={brandingColor} onChange={(e) => setBrandingColor(e.target.value)} className="w-12 h-12 rounded-lg cursor-pointer border-0 bg-transparent" /><input type="text" value={brandingColor} onChange={(e) => setBrandingColor(e.target.value)} placeholder="#a855f7" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition font-mono uppercase" /></div>
            </div>
            <div className="space-y-3">
              <label className="block text-white/60 text-sm">Background Image (Public Page)</label>
              <div className="flex items-center gap-4">
                <div className="w-32 h-20 bg-black/40 rounded-xl flex items-center justify-center overflow-hidden border border-white/10">{brandingData?.background_url ? <img src={brandingData.background_url} alt="Background" className="w-full h-full object-cover" /> : <Image className="w-6 h-6 text-white/30" />}</div>
                <div className="flex-1 space-y-2">
                  <label className="block"><input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleBackgroundUpload} className="hidden" id="background-upload" /><span className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-4 py-2 rounded-lg transition cursor-pointer inline-block text-sm">{uploadingBackground ? 'Uploading...' : 'Upload Background'}</span></label>
                  <p className="text-white/40 text-xs">JPG, PNG, GIF or WebP. Max 10MB</p>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <label className="block text-white/60 text-sm">Preview</label>
              <div className="bg-black/40 rounded-xl p-4 border border-white/10" style={{ borderColor: brandingColor + '40' }}>
                <div className="flex items-center gap-3">
                  {brandingData?.logo_url ? <img src={brandingData.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: brandingColor + '30' }}><Radio className="w-5 h-5" style={{ color: brandingColor }} /></div>}
                  <div><p className="text-white font-semibold text-sm">{currentUser.station_name || 'Your Station'}</p><p className="text-white/50 text-xs">{brandingTagline || 'Your tagline here...'}</p></div>
                </div>
              </div>
            </div>
          </div>
          {/* Player Customization */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <h4 className="text-white font-semibold mb-4 flex items-center gap-2"><Palette className="w-4 h-4 text-purple-400" /> Player Customization</h4>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-3">
                <label className="block text-white/60 text-sm">Player Card Color</label>
                <div className="flex items-center gap-3"><input type="color" value={playerConfig.player_card_color || '#1a1a3e'} onChange={(e) => setPlayerConfig(p => ({ ...p, player_card_color: e.target.value }))} className="w-12 h-12 rounded-lg cursor-pointer border-0 bg-transparent" /><input type="text" value={playerConfig.player_card_color || '#1a1a3e'} onChange={(e) => setPlayerConfig(p => ({ ...p, player_card_color: e.target.value }))} className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white font-mono uppercase focus:outline-none focus:border-purple-500 transition text-sm" /></div>
              </div>
              <div className="space-y-4">
                <label className="block text-white/60 text-sm">Player Features</label>
                {[
                  { key: 'show_fft', icon: BarChart3, label: 'FFT Visualizer' },
                  { key: 'show_history', icon: Clock, label: 'Song History' },
                  { key: 'show_listeners', icon: Users, label: 'Listener Count' },
                ].map(({ key, icon: Icon, label }) => (
                  <label key={key} className="flex items-center gap-3 cursor-pointer group">
                    <div className={`w-10 h-6 rounded-full transition-colors relative ${playerConfig[key] ? 'bg-purple-500' : 'bg-white/10'}`} onClick={() => setPlayerConfig(p => ({ ...p, [key]: !p[key] }))}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${playerConfig[key] ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                    </div>
                    <div className="flex items-center gap-2 text-white/70 group-hover:text-white transition text-sm"><Icon className="w-4 h-4" /> {label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <button onClick={handleSaveBranding} disabled={savingBranding} className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl transition">{savingBranding ? 'Saving...' : 'Save Branding'}</button>
              {brandingSuccess && <span className="text-green-400 text-sm">{brandingSuccess}</span>}
              {brandingError && <span className="text-red-400 text-sm">{brandingError}</span>}
            </div>
            <a href={`https://djsandmcmedia.com/listen/${currentUser.azuracast_station_id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm transition"><ExternalLink className="w-4 h-4" /> View Public Page</a>
          </div>
        </div>
      </>
    ) : stationRequest?.status === 'pending' ? (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
        <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6"><Clock className="w-10 h-10 text-yellow-400" /></div>
        <h3 className="text-2xl font-bold text-white mb-2">Request Pending</h3>
        <p className="text-white/60 mb-6 max-w-md mx-auto">Your station request for "<span className="text-purple-400">{stationRequest.station_name}</span>" is being processed. We'll set it up shortly!</p>
        <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-6 max-w-md mx-auto mb-6">
          <p className="text-yellow-400 text-lg font-bold mb-4">Payment Required</p>
          {stationRequest.payment_method === 'paypal' ? (
            <div className="text-left space-y-3"><p className="text-white/80 text-sm">Send payment via <span className="text-blue-400 font-semibold">PayPal</span> to:</p><div className="bg-black/40 rounded-lg p-3"><p className="text-white font-mono text-lg select-all">wnuv98.5@gmail.com</p></div><p className="text-white/50 text-xs">Include your email address in the payment note</p></div>
          ) : (
            <div className="text-left space-y-3"><p className="text-white/80 text-sm">Send payment via <span className="text-green-400 font-semibold">CashApp</span> to:</p><div className="bg-black/40 rounded-lg p-3"><p className="text-white font-mono text-lg select-all">$garynuhayden1</p></div><p className="text-white/50 text-xs">Include your email address in the payment note</p></div>
          )}
        </div>
        <div className="text-white/40 text-sm">Submitted: {new Date(stationRequest.created_at).toLocaleDateString()}</div>
      </div>
    ) : (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6"><Radio className="w-10 h-10 text-purple-400" /></div>
          <h3 className="text-2xl font-bold text-white mb-2">Request Your Station</h3>
          <p className="text-white/60 max-w-md mx-auto">Submit a request and we'll set up your radio station. Your <span className="text-purple-400 capitalize">{currentUser.plan === 'premium' ? 'Premium' : currentUser.plan}</span> plan includes:</p>
        </div>
        <div className="flex justify-center gap-6 mb-8 text-sm">
          <div className="text-center"><p className="text-2xl font-bold text-purple-400">{currentUser.plan === 'starter' ? '100' : currentUser.plan === 'professional' ? '500' : '∞'}</p><p className="text-white/50">Listeners</p></div>
          <div className="text-center"><p className="text-2xl font-bold text-pink-400">{currentUser.plan === 'starter' ? '128' : '320'}kbps</p><p className="text-white/50">Quality</p></div>
          <div className="text-center"><p className="text-2xl font-bold text-blue-400">{currentUser.plan === 'starter' ? '5' : currentUser.plan === 'professional' ? '50' : '∞'}GB</p><p className="text-white/50">Storage</p></div>
        </div>
        {submitError && <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-xl mb-4 text-sm max-w-md mx-auto">{submitError}</div>}
        {submitSuccess && <div className="bg-green-500/20 border border-green-500/50 text-green-300 px-4 py-3 rounded-xl mb-4 text-sm max-w-md mx-auto">{submitSuccess}</div>}
        <div className="max-w-md mx-auto space-y-4">
          <div><label className="block text-white/70 text-sm mb-2">Station Name</label><input type="text" value={stationNameInput} onChange={(e) => setStationNameInput(e.target.value)} placeholder="My Awesome Radio" data-testid="station-name-input" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition" /></div>
          <div><label className="block text-white/70 text-sm mb-2">Payment Method</label>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setPaymentMethod('paypal')} className={`py-3 px-4 rounded-xl text-sm font-medium transition flex items-center justify-center gap-2 ${paymentMethod === 'paypal' ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>PayPal</button>
              <button type="button" onClick={() => setPaymentMethod('cashapp')} className={`py-3 px-4 rounded-xl text-sm font-medium transition flex items-center justify-center gap-2 ${paymentMethod === 'cashapp' ? 'bg-green-500 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>CashApp</button>
            </div>
          </div>
          <button onClick={handleRequestStation} disabled={submitting} data-testid="request-station-btn" className="w-full bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl transition flex items-center justify-center gap-2">{submitting ? (<><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Submitting...</>) : (<><Zap className="w-5 h-5" /> Request My Station</>)}</button>
          <p className="text-white/40 text-xs text-center">After submitting, you'll receive payment instructions. Once payment is confirmed, we'll set up your station.</p>
        </div>
        <div className="mt-8 pt-8 border-t border-white/10">
          <h4 className="text-white font-medium mb-4 text-center">Already have a station ID?</h4>
          <div className="max-w-md mx-auto flex gap-2">
            <input type="text" value={stationIdInput} onChange={(e) => setStationIdInput(e.target.value)} placeholder="Enter your station ID" data-testid="station-id-input" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition" />
            <button onClick={handleConnectStation} disabled={submitting} data-testid="connect-station-btn" className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl transition">Connect</button>
          </div>
        </div>
      </div>
    )}
  </div>
  );
};
